/**
 * API client — speaks the §2.4 envelope and nothing else.
 *
 * On `TOKEN_EXPIRED` it silently refreshes once and replays the request; a
 * second failure bubbles up so the auth layer can send the user to /login.
 */

import type { ApiMeta } from '@virtuetasks/types';

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: { field: string; message: string }[];
  readonly requestId?: string;

  constructor(
    code: string,
    message: string,
    status: number,
    details?: { field: string; message: string }[],
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAuthLostHandler(fn: (() => void) | null): void {
  onAuthLost = fn;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the automatic refresh-and-retry (used by the refresh call itself). */
  noRetry?: boolean;
  /** Extra headers such as `If-Match` or `Idempotency-Key`. */
  headers?: Record<string, string>;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { body, headers = {}, noRetry, ...rest } = options;

  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 204) return { data: undefined as T };

  const payload = (await res.json().catch(() => null)) as
    | { success: true; data: T; meta?: ApiMeta }
    | { success: false; error: { code: string; message: string; details?: { field: string; message: string }[]; requestId?: string } }
    | null;

  if (!payload) {
    throw new ApiError('INTERNAL_ERROR', `Unexpected response (${res.status})`, res.status);
  }

  if (payload.success) return { data: payload.data, meta: payload.meta };

  const { code, message, details, requestId } = payload.error;

  if (code === 'TOKEN_EXPIRED' && !noRetry) {
    const refreshed = await refreshOnce();
    if (refreshed) return rawRequest<T>(path, { ...options, noRetry: true });
    onAuthLost?.();
  }
  if (res.status === 401 && !noRetry) onAuthLost?.();

  throw new ApiError(code, message, res.status, details, requestId);
}

/** Concurrent 401s share one refresh flight. */
async function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) return false;
        const payload = (await res.json()) as { success: boolean; data?: { accessToken: string } };
        if (!payload.success || !payload.data) return false;
        accessToken = payload.data.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();
  }
  return refreshPromise;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    rawRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    rawRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    rawRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    rawRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    rawRequest<T>(path, { ...options, method: 'DELETE', body }),
  raw: rawRequest,
};

/** Build a query string, dropping empty values so URLs stay shareable. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' ) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
      continue;
    }
    if (value instanceof Date) {
      search.set(key, value.toISOString());
      continue;
    }
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
